// src/components/VibeCoding.js
import React, { useState, useEffect, useRef } from 'react';
import { Code2, AlertCircle, Folder, FileText, Paperclip, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import ChatContainer from './ChatContainer';
import ModelSelector from './ModelSelector';
import SessionTracker from './SessionTracker';
import ChatSidebar from './ChatSidebar';
import ProjectSelector from './ProjectSelector';
import FileExplorer from './FileExplorer';
import ConnectionWarning from './ConnectionWarning';
import { useVibeCoding } from '../hooks/useVibeCoding';
import { useAuth } from '../hooks/useAuth';
import { useTokenCalculator } from '../hooks/useTokenCalculator';
import { useFileUpload } from '../hooks/useFileUpload';
import TokenDisplay from './TokenDisplay';
import './VibeCoding.css';

const VibeCoding = ({ showHistory, onCloseHistory }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [isAISelectingFiles, setIsAISelectingFiles] = useState(false);
  const [aiSelectedFiles, setAiSelectedFiles] = useState([]);

  const handleAIFileSelection = async () => {
    if (!inputMessage.trim() || !isConnected || !projectPath) {
      const errorMsg = {
        role: 'assistant',
        content: `🚨 **AI File Selection Error**\n\n${!inputMessage.trim() ? 'Please enter a message first' : !isConnected ? 'Not connected to Brimind VIBE. Please check connection.' : 'No project selected. Please select a project first.'}`,
        timestamp: new Date().toISOString(),
        isSystemMessage: true,
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    setIsAISelectingFiles(true);
    
    // Add starting message
    const startingMsg = {
      role: 'assistant',
      content: `🤖 **AI File Selection Started**\n\nAnalyzing your request: "${inputMessage.slice(0, 100)}${inputMessage.length > 100 ? '...' : ''}"\n\nScanning project files...`,
      timestamp: new Date().toISOString(),
      isSystemMessage: true
    };
    setMessages(prev => [...prev, startingMsg]);

    try {
      // First, get all project files from localhost:3007
      const projectFilesResponse = await fetch(
        `http://localhost:3007/api/file-paths?project=${encodeURIComponent(projectPath)}`,
        { headers: { 'Content-Type': 'application/json' }, mode: 'cors' }
      );
      
      if (!projectFilesResponse.ok) {
        const errorDetails = projectFilesResponse.status === 404 
          ? 'Project not found' 
          : projectFilesResponse.status === 500 
          ? 'Server error' 
          : `HTTP ${projectFilesResponse.status}`;
        throw new Error(`Failed to fetch project files: ${errorDetails}`);
      }
      
      const projectFilesData = await projectFilesResponse.json();
      const allFilePaths = projectFilesData.filePaths || [];
      
      if (allFilePaths.length === 0) {
        const errorMsg = {
          role: 'assistant',
          content: `🚨 **AI File Selection Error**\n\nNo files found in project: ${projectPath}\n\nPlease check if the project path is correct and contains files.`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true,
          isError: true
        };
        setMessages(prev => [...prev, errorMsg]);
        setIsAISelectingFiles(false);
        return;
      }

      // Update message with file count
      const scanningMsg = {
        role: 'assistant',
        content: `🤖 **Project Scanned**\n\nFound ${allFilePaths.length} files in project.\n\nSending to AI for analysis...`,
        timestamp: new Date().toISOString(),
        isSystemMessage: true
      };
      setMessages(prev => [...prev, scanningMsg]);

      // Send user message + all file paths to AI for selection
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/vibecoding/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          model: selectedModel,
          message: `${inputMessage}\n\n=== AVAILABLE PROJECT FILES ===\n${allFilePaths.join('\n')}`,
          conversation: [],
          projectContext: projectPath ? `\n\nCurrent project: ${projectPath}` : '',
          isFileSelection: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorDetails = errorData.error 
          ? (typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error))
          : `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(`AI service error: ${errorDetails}`);
      }

      const data = await response.json();
      let neededFiles = [];
      
      try {
        const aiMessage = data.message || data.explanationText || '';
        const jsonMatch = aiMessage.match(/\{[\s\S]*neededFiles[\s\S]*\}/i);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          neededFiles = parsed.neededFiles || [];
        } else {
          // Fallback: look for file mentions in text
          const lines = aiMessage.split('\n');
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (allFilePaths.some(path => trimmedLine.includes(path))) {
              const foundPath = allFilePaths.find(path => trimmedLine.includes(path));
              if (foundPath && !neededFiles.includes(foundPath)) {
                neededFiles.push(foundPath);
              }
            }
          }
        }
      } catch (parseError) {
        const errorMsg = {
          role: 'assistant',
          content: `🚨 **AI Response Parsing Error**\n\nFailed to parse AI response for file selection.\n\nError: ${parseError.message}\n\nAI Response Preview: ${(data.message || data.explanationText || 'No response').slice(0, 200)}...`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true,
          isError: true
        };
        setMessages(prev => [...prev, errorMsg]);
        setIsAISelectingFiles(false);
        return;
      }

      if (neededFiles.length > 0) {
        // Filter out files that don't actually exist in the project
        const validFiles = neededFiles.filter(file => allFilePaths.includes(file));
        const invalidFiles = neededFiles.filter(file => !allFilePaths.includes(file));
        
        if (validFiles.length > 0) {
          // Select these files in the UI (like user selected them)
          setSelectedFiles(validFiles);
          setAiSelectedFiles(validFiles);
          setShowFileExplorer(true);
          
          let successContent = `✅ **AI File Selection Complete**\n\nSelected ${validFiles.length} files for your request:\n\n${validFiles.map(f => `• ${f}`).join('\n')}\n\nThese files are now selected and will be included when you send your message.`;
          
          if (invalidFiles.length > 0) {
            successContent += `\n\n⚠️ **Note:** ${invalidFiles.length} files mentioned by AI were not found in the project:\n${invalidFiles.map(f => `• ${f}`).join('\n')}`;
          }
          
          const successMsg = {
            role: 'assistant',
            content: successContent,
            timestamp: new Date().toISOString(),
            isSystemMessage: true
          };
          setMessages(prev => [...prev, successMsg]);
        } else {
          const errorMsg = {
            role: 'assistant',
            content: `🚨 **AI File Selection Error**\n\nAI selected ${neededFiles.length} files, but none exist in the project:\n\n${neededFiles.map(f => `• ${f}`).join('\n')}\n\nPlease try rephrasing your request or check if the files exist in the project.`,
            timestamp: new Date().toISOString(),
            isSystemMessage: true,
            isError: true
          };
          setMessages(prev => [...prev, errorMsg]);
        }
      } else {
        const errorMsg = {
          role: 'assistant',
          content: `🚨 **AI File Selection Error**\n\nAI could not determine which files are needed for your request.\n\nThis might happen because:\n• The request is too vague\n• No specific files are needed\n• AI response format was unexpected\n\nTry being more specific about which files or components you want to work with.`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true,
          isError: true
        };
        setMessages(prev => [...prev, errorMsg]);
      }
    } catch (error) {
      console.error('AI file selection failed:', error);
      
      let errorContent = `🚨 **AI File Selection Failed**\n\n`;
      
      if (error.message.includes('Failed to fetch')) {
        if (error.message.includes('localhost:3007')) {
          errorContent += `**Connection Error**\n\nCannot connect to Brimind VIBE (localhost:3007).\n\nPlease check:\n• Is Brimind VIBE application running?\n• Is the port 3007 available?\n• Is your firewall blocking the connection?`;
        } else {
          errorContent += `**Network Error**\n\nFailed to connect to AI service.\n\nPlease check your internet connection and try again.`;
        }
      } else if (error.message.includes('AI service error')) {
        errorContent += `**AI Service Error**\n\n${error.message}\n\nThe AI service encountered an issue processing your request.`;
      } else {
        errorContent += `**Unexpected Error**\n\n${error.message}\n\nPlease try again or contact support if the problem persists.`;
      }
      
      const errorMsg = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
        isSystemMessage: true,
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsAISelectingFiles(false);
  };
  
  // Redirect mobile users to AI Chat
  useEffect(() => {
    if (isMobile) {
      navigate('/');
      return;
    }
  }, [isMobile, navigate]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle desktop sidebar toggle
  useEffect(() => {
    if (!isMobile && showHistory !== undefined) {
      setDesktopSidebarOpen(showHistory);
    }
  }, [showHistory, isMobile]);
  
  // Set up global toggle functions
  useEffect(() => {
    window.toggleProjectSelector = () => {
      console.log('VibeCoding toggleProjectSelector called');
      setShowProjectSelector(prev => !prev);
    };

    window.toggleFileExplorer = () => {
      console.log('VibeCoding toggleFileExplorer called');
      setShowFileExplorer(prev => !prev);
    };

    if (!isMobile) {
      window.toggleDesktopHistory = () => {
        console.log('VibeCoding desktop history toggle called');
        if (onCloseHistory) {
          onCloseHistory();
        }
      };
    }
    
    return () => {
      if (window.toggleProjectSelector) {
        delete window.toggleProjectSelector;
      }
      if (window.toggleFileExplorer) {
        delete window.toggleFileExplorer;
      }
      if (window.toggleDesktopHistory) {
        delete window.toggleDesktopHistory;
      }
    };
  }, [isMobile, onCloseHistory]);
  const {
    // State
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    selectedModel,
    setSelectedModel,
    isLoading,
    isConnected,
    projectPath,
    setProjectPath,
    projectFiles,
    selectedFiles,
    setSelectedFiles,
    includeCurrentFiles,
    setIncludeCurrentFiles,
    allProjectFiles,
    chatHistory,
    currentChatId,
    // UI State
    showProjectSelector,
    setShowProjectSelector,
    showFileExplorer,
    setShowFileExplorer,
    // Actions
    sendMessage,
    checkConnection,
    fetchProjectFiles,
    createNewChat,
    loadChat,
    deleteChat
  } = useVibeCoding();

  const messagesEndRef = useRef(null);
  
  // Handle include files toggle - deselect individual files when include all is enabled
  useEffect(() => {
    if (includeCurrentFiles && selectedFiles.length > 0) {
      // Clear all selected files when include all is enabled
      setSelectedFiles([]);
    }
  }, [includeCurrentFiles, selectedFiles.length, setSelectedFiles]);
  
  const sessionConfig = {
    maxTokensPerSession: 100000,
    tokensPerHour: 500,
    membershipLevel: 'basic'
  };

  const [modelsData, setModelsData] = useState({ models: [] });
  
  // Load models data for file upload capabilities
  useEffect(() => {
    const loadModelsData = async () => {
      try {
        const vibeResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/models/vibecoding`, {
          credentials: 'include'
        });
        if (vibeResponse.ok) {
          const data = await vibeResponse.json();
          setModelsData(data);
          window.modelsCache = data;
        }
      } catch (error) {
        console.error('Failed to load models data:', error);
      }
    };
    loadModelsData();
  }, []);

  const {
    attachments,
    setAttachments,
    handleDrop: handleFileDrop,
    handleDragOver,
    handlePaste,
    removeAttachment,
    supportsVision,
  } = useFileUpload(selectedModel, modelsData);

  const tokenStats = useTokenCalculator(
    messages,
    inputMessage,
    selectedModel,
    attachments,
    allProjectFiles,
    includeCurrentFiles,
    selectedFiles
  );
  
  // Log token calculation details for debugging
  useEffect(() => {
    if (selectedFiles.length > 0 || includeCurrentFiles) {
      console.log('Token calculation context:', {
        selectedFilesCount: selectedFiles.length,
        includeCurrentFiles,
        projectFilesTokens: tokenStats.projectFilesTokens,
        totalTokens: tokenStats.totalTokens
      });
    }
  }, [selectedFiles.length, includeCurrentFiles, tokenStats.projectFilesTokens, tokenStats.totalTokens]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check if we should show token display
  const shouldShowTokenDisplay = inputMessage.trim() !== "" || attachments.length > 0;

  return (
    <>
      {/* Fixed Toolbar for VibeCoding */}
      <div className="vibe-toolbar">
        <div className="toolbar-content">
          <button 
            className="toolbar-btn"
            onClick={() => setShowProjectSelector(!showProjectSelector)}
            disabled={!isConnected}
            title="Select Project"
          >
            <Folder size={18} />
            <span>Project</span>
          </button>
          
          <button 
            className="toolbar-btn"
            onClick={() => setShowFileExplorer(!showFileExplorer)}
            disabled={!isConnected || !projectPath}
            title="File Explorer"
          >
            <FileText size={18} />
            <span>Files</span>
          </button>

          <button 
            className="toolbar-btn ai-select-btn"
            onClick={handleAIFileSelection}
            disabled={!inputMessage.trim() || !isConnected || !projectPath || isAISelectingFiles}
            title="Let AI select relevant files for your request"
          >
            <span>🤖</span>
            <span>{isAISelectingFiles ? 'AI Selecting...' : 'AI Select Files'}</span>
          </button>
          
          <div className="toolbar-spacer"></div>
          
          <div className="include-files-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={includeCurrentFiles || false}
                onChange={(e) => setIncludeCurrentFiles(e.target.checked)}
                className="toggle-input"
                disabled={!projectPath}
              />
              <span className="toggle-text">
                Include all project files
                {includeCurrentFiles && <span className="files-loaded"> ✓ Loaded</span>}
              </span>
            </label>
          </div>
        </div>
        
        {/* Project Selector Panel */}
        {showProjectSelector && isConnected && (
          <div className="toolbar-panel">
            <ProjectSelector
              projectPath={projectPath}
              setProjectPath={setProjectPath}
              onClose={() => setShowProjectSelector(false)}
              onSave={(path) => {
                setProjectPath(path);
                fetchProjectFiles(path);
                setShowProjectSelector(false);
              }}
            />
          </div>
        )}
        
        {/* File Explorer Panel */}
        {showFileExplorer && isConnected && (
          <div className="toolbar-panel">
            <FileExplorer
              projectFiles={projectFiles}
              selectedFiles={selectedFiles}
              aiSelectedFiles={aiSelectedFiles}
              onToggleFile={(filePath) => {
                console.log('Toggling file:', filePath);
                setSelectedFiles(prev => {
                  const newFiles = prev.includes(filePath) 
                    ? prev.filter(f => f !== filePath)
                    : [...prev, filePath];
                  console.log('New selected files:', newFiles);
                  return newFiles;
                });
                if (includeCurrentFiles) {
                  setIncludeCurrentFiles(false);
                }
              }}
              onClose={() => setShowFileExplorer(false)}
            />
          </div>
        )}
      </div>
      
      <Layout
        title="Vibe Coding"
        icon={<Code2 size={32} />}
        headerActions={
          <>
            <div className="status-container">
              {isConnected ? (
                <span className="connection-status connected">● Connected to Brimind Vibe</span>
              ) : (
                <span className="connection-status disconnected">● Disconnected</span>
              )}
              {projectPath && (
                <span className="project-path">Project: {projectPath}</span>
              )}
            </div>
      
          </>
        }
        className="vibe-coding-layout"
      >
      {!isConnected && (
        <ConnectionWarning onRetryConnection={checkConnection} />
      )}
   
      <ChatSidebar
        chatHistory={chatHistory}
        currentChatId={currentChatId}
        onLoadChat={loadChat}
        onDeleteChat={deleteChat}
        onCreateNewChat={createNewChat}
        isOpen={isMobile ? showHistory : desktopSidebarOpen}
        onToggle={() => {
          console.log('VibeCoding ChatSidebar onToggle called, isMobile:', isMobile, 'showHistory:', showHistory);
          if (isMobile) {
            // For VibeCoding, we need to call the parent's toggle function
            if (onCloseHistory) {
              onCloseHistory();
            }
          } else {
            if (onCloseHistory) {
              onCloseHistory();
            }
          }
        }}
      />

      {showFileExplorer && isConnected && (
        <div className="overlay-panel">
          <FileExplorer
            projectFiles={projectFiles}
            selectedFiles={selectedFiles}
            onToggleFile={(filePath) => {
              setSelectedFiles(prev => 
                prev.includes(filePath) 
                  ? prev.filter(f => f !== filePath)
                  : [...prev, filePath]
              );
              if (includeCurrentFiles) {
                setIncludeCurrentFiles(false);
              }
            }}
            onClose={() => setShowFileExplorer(false)}
          />
        </div>
      )}

      <ChatContainer
        messages={messages}
        isLoading={isLoading}
        onSendMessage={() => sendMessage(attachments, setAttachments)}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        attachments={attachments}
        onFileUpload={(e) => {
          const files = Array.from(e.target.files);
          files.forEach((file) => setAttachments((prev) => [...prev, file]));
        }}
        onRemoveAttachment={removeAttachment}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        onPaste={handlePaste}
        disabled={!isConnected}
        placeholder={isConnected ? (projectPath ? "Describe what you want to build or modify..." : "Describe what you want to build or select a project first...") : "Connect to localhost:3007 first"}
        selectedModel={selectedModel}
        supportsVision={supportsVision}
        tokenStats={tokenStats}
        onInputChange={setInputMessage}
        includeCurrentFiles={includeCurrentFiles}
        onToggleIncludeFiles={setIncludeCurrentFiles}
        hideInputArea={true}
      />

      <div className={`bottom-navigation ${isMobile ? (showHistory ? 'sidebar-open' : '') : (desktopSidebarOpen ? 'sidebar-open' : '')}`}>
        {shouldShowTokenDisplay && (
          <div className="token-display-section">
            <TokenDisplay tokenStats={tokenStats} />
          </div>
        )}
        <div className="input-section">
          <div className="input-area">
            {attachments.length > 0 && (
              <div className="attachments-preview">
                {attachments.map((attachment, index) => (
                  <div key={index} className="attachment-item">
                    {attachment.type === 'image' ? (
                      <img 
                        src={attachment.data} 
                        alt={attachment.name}
                        className="attachment-thumb"
                      />
                    ) : (
                      <div className="file-attachment">
                        <Paperclip size={16} />
                        <span>{attachment.name}</span>
                      </div>
                    )}
                    <button 
                      className="remove-attachment"
                      onClick={() => removeAttachment(index)}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="input-row">
              <button 
                className="attachment-btn"
                onClick={() => {
                  const fileInput = document.createElement('input');
                  fileInput.type = 'file';
                  fileInput.multiple = true;
                  fileInput.accept = (() => {
                    const acceptedTypes = [];
                    const modelsCache = window.modelsCache;
                    if (modelsCache && selectedModel) {
                      const model = modelsCache.models?.find(m => m.id === selectedModel);
                      if (model && model.acceptedFiles) {
                        if (model.acceptedFiles.includes('image')) acceptedTypes.push('image/*');
                        if (model.acceptedFiles.includes('pdf')) acceptedTypes.push('.pdf');
                        if (model.acceptedFiles.includes('docx')) acceptedTypes.push('.doc,.docx');
                        if (model.acceptedFiles.includes('txt')) acceptedTypes.push('.txt');
                        if (model.acceptedFiles.includes('csv')) acceptedTypes.push('.csv');
                      }
                    }
                    return acceptedTypes.length > 0 ? acceptedTypes.join(',') : '.txt';
                  })();
                  fileInput.onchange = (e) => {
                    const files = Array.from(e.target.files);
                    files.forEach((file) => setAttachments((prev) => [...prev, file]));
                  };
                  fileInput.click();
                }}
                title={(() => {
                  const modelsCache = window.modelsCache;
                  if (modelsCache && selectedModel) {
                    const model = modelsCache.models?.find(m => m.id === selectedModel);
                    if (model && model.acceptedFiles) {
                      const types = model.acceptedFiles.join(', ');
                      return `Upload files: ${types}`;
                    }
                  }
                  return supportsVision ? "Upload files or images" : "Upload text files only (this model doesn't support images)";
                })()}
              >
                <Paperclip size={20} />
              </button>
              
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={isConnected ? (projectPath ? "Describe what you want to build or modify..." : "Describe what you want to build or select a project first...") : "Connect to localhost:3007 first"}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(attachments, setAttachments);
                  }
                }}
                onPaste={handlePaste}
                className="message-input"
                rows="1"
                disabled={!isConnected}
              />
              
              <button 
                onClick={() => sendMessage(attachments, setAttachments)}
                disabled={!inputMessage.trim() || !selectedModel || !isConnected}
                className="send-btn"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
        <div className="model-selector-section">
          <ModelSelector
            selectedModel={selectedModel}
            onModelSelect={setSelectedModel}
            hasAttachments={attachments.length > 0}
            userBalance={user?.balance || 0}
            mode="vibecoding"
          />
        </div>
      </div>
      </Layout>
    </>
  );
};

export default VibeCoding;